import React, { Component } from 'react';

import findIndex from 'lodash/findIndex';

import TestCaseBox from '@qality/common-components/src/test-case-box/TestCaseBox';
import {
  LastExecution,
  TestCase,
  TestCasesCollection,
} from '@qality/common-model';
import { TestCaseFullScreenCloseCallbackData } from '@qality/common-model/src/TestCaseFullScreenData';
import {
  StorageOfUniqueServices,
  TestCasesService,
  TestExecutionService,
  ViewService,
} from '@qality/common-service';
import { Utils } from '@qality/common-utils';

import NoTestCases from './NoTestCases';
import './_test-cases.scss';

type Props = {
  onUpdateTestCases: (value: TestCasesCollection) => void;
  issueKey: string;
  testCases: TestCasesCollection;
  canEdit: boolean;
  expandOneTestCase: (testCaseIndex: number, value: boolean) => void;
  syncDataFromFullScreen: (data: TestCaseFullScreenCloseCallbackData) => void;
  onDeleteTestCaseLink?: (testCaseId: number) => void;
};

type State = {
  lastExecutions: Array<LastExecution>;
};

export default class TestCases extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { lastExecutions: [] };
  }

  componentDidMount = async () => {
    await this.fetchLastExecution();
  };

  mapTestCasesToComponents = (): React.ReactNode => {
    return this.props.testCases.testCases.map((testCase, testCaseIndex) => {
      const testCaseLastExecution = this.state.lastExecutions.find(
        (execution) => execution.testCaseId.toString() === testCase.id
      );

      return (
        <TestCaseBox
          key={testCase.id}
          testCase={testCase}
          canEdit={this.props.canEdit}
          onUpdateTestCase={this.updateTestCase}
          testCaseIndex={testCaseIndex}
          deleteTestCase={this.deleteTestCase}
          view={ViewService.fetchView(this.props.issueKey)}
          issueKey={this.props.issueKey}
          expanded={testCase.expanded}
          expandOneTestCase={this.props.expandOneTestCase}
          syncDataFromFullScreen={this.props.syncDataFromFullScreen}
          lastExecution={testCaseLastExecution}
        />
      );
    });
  };

  getTestCaseIndex(id: number): number {
    const testCases = this.props.testCases;
    const index = findIndex(testCases.testCases, (testCase) => {
      return testCase.id === id;
    });
    return index;
  }

  deleteTestCase = (testCase: TestCase): void => {
    const testCasesCollection = new TestCasesCollection([
      ...this.props.testCases.testCases,
    ]);
    const index = this.getTestCaseIndex(testCase.id);
    testCasesCollection.testCases.splice(index, 1);
    this.props.onUpdateTestCases(testCasesCollection);
    TestCasesService.deleteTestCaseLink(testCase, this.props.issueKey);

    if (this.props.onDeleteTestCaseLink) {
      this.props.onDeleteTestCaseLink(Number(testCase.id));
    }
  };

  updateTestCase = (
    id: number,
    testCase: TestCase,
    updateOnlyState = false
  ): void => {
    const testCasesCollection = new TestCasesCollection([
      ...this.props.testCases.testCases,
    ]);
    const index = this.getTestCaseIndex(id);
    testCasesCollection.testCases[index] = testCase;
    this.props.onUpdateTestCases(testCasesCollection);
    if (!updateOnlyState) {
      TestCasesService.updateTestCase(testCase);
    }
  };

  fetchLastExecution = async () => {
    try {
      const testCaseIds = this.props.testCases.testCases.map(
        (testCase) => testCase.id
      );

      const lastExecutions =
        await TestExecutionService.getLastTestExecutionData(testCaseIds);

      this.setState({ lastExecutions });
    } catch (e) {
      StorageOfUniqueServices.get('ErrorHandlingUtils').handleError(
        e,
        'We could not retrieve last execution details. Please try again. If the problem persists, feel free to contact our support.'
      );
    }
  };

  render(): React.ReactNode {
    return (
      <div className='test-cases-panel'>
        {Utils.isNilOrEmpty(this.props.testCases.testCases) ? (
          <NoTestCases />
        ) : (
          this.mapTestCasesToComponents()
        )}
      </div>
    );
  }
}
